/**
 * Vẽ một ô watermark ra PNG (data URL) để lặp làm nền — cách antd Watermark /
 * watermark-js-plus làm: một <canvas> nhỏ theo devicePixelRatio, CSS
 * background-repeat lo phần còn lại (rẻ hơn hàng trăm thẻ <span>).
 *
 * Ô gồm 2 hàng xếp gạch: hàng dưới lệch nửa ô, chữ ở mép trái/phải bị cắt đôi
 * sẽ ghép liền khi lặp — một mẩu cắt nhỏ của ảnh chụp vẫn có trọn một dòng mã.
 */

export interface TileOptions {
  lines: string[];
  /** Alpha của chữ (0–1). */
  opacity: number;
  /** Độ nghiêng (độ), âm = lên phải. */
  angle: number;
  fontSize: number;
  fontWeight: number;
  /** Màu mực RGB, vd "27,33,80". */
  ink: string;
  /** Vẽ thêm bóng trắng cùng độ mờ để còn thấy trên nền tối. */
  halo: boolean;
  gapX: number;
  gapY: number;
}

export interface Tile {
  url: string;
  width: number;
  height: number;
}

export const FONT_STACK = '"Be Vietnam Pro", "Segoe UI", system-ui, -apple-system, sans-serif';

/** Lớp nhìn thấy: chữ nhỏ, nghiêng -22° (antd/Lark), mực navy Đêm + bóng trắng. */
export const VISIBLE_TILE: Omit<TileOptions, 'lines' | 'opacity'> = {
  angle: -22,
  fontSize: 12,
  // 500, không 600: 29/09 chủ dự án thấy chữ còn đậm.
  fontWeight: 500,
  ink: '27,33,80',
  halo: true,
  gapX: 56,
  gapY: 44,
};

/** Lớp ẩn: chỉ mã, chữ to đậm, nghiêng ngược lớp trên, alpha rất thấp. */
export const HIDDEN_TILE: Omit<TileOptions, 'lines' | 'opacity'> = {
  angle: 18,
  fontSize: 22,
  fontWeight: 800,
  ink: '0,0,0',
  halo: true,
  gapX: 80,
  gapY: 60,
};

export function renderTile(options: TileOptions, dpr = window.devicePixelRatio || 1): Tile | null {
  const lines = options.lines.filter(Boolean);
  if (!lines.length || options.opacity <= 0) return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const font = `${options.fontWeight} ${options.fontSize}px ${FONT_STACK}`;
  ctx.font = font;
  const lineHeight = Math.round(options.fontSize * 1.45);
  const textW = Math.max(1, ...lines.map((line) => ctx.measureText(line).width));
  const textH = lineHeight * lines.length;
  const rad = (options.angle * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const cellW = Math.ceil(textW * cos + textH * sin + options.gapX);
  const cellH = Math.ceil(textW * sin + textH * cos + options.gapY);
  const width = cellW * 2;
  const height = cellH * 2;
  const scale = Math.min(Math.max(dpr, 1), 3);

  canvas.width = Math.ceil(width * scale);
  canvas.height = Math.ceil(height * scale);
  ctx.scale(scale, scale);

  const draw = (cx: number, cy: number) => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rad);
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    lines.forEach((line, index) => {
      const y = (index - (lines.length - 1) / 2) * lineHeight;
      if (options.halo) {
        // Bóng trắng nhạt hơn mực một chút: đủ thấy trên nền tối, không làm nét dày.
        ctx.fillStyle = `rgba(255,255,255,${options.opacity * 0.8})`;
        ctx.fillText(line, 1, y + 1);
      }
      ctx.fillStyle = `rgba(${options.ink},${options.opacity})`;
      ctx.fillText(line, 0, y);
    });
    ctx.restore();
  };

  // Hàng trên: 2 chữ giữa ô. Hàng dưới lệch nửa ô: chữ ở x = 0 và x = width
  // mỗi chữ chỉ hiện một nửa, lặp nền thì ghép lại thành một.
  draw(cellW * 0.5, cellH * 0.5);
  draw(cellW * 1.5, cellH * 0.5);
  draw(0, cellH * 1.5);
  draw(cellW, cellH * 1.5);
  draw(width, cellH * 1.5);

  return { url: canvas.toDataURL('image/png'), width, height };
}

/** "28/09/2026 23:05" theo đồng hồ máy người xem. */
export function formatWatermarkTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export interface WatermarkPayload {
  enabled: boolean;
  lines?: string[];
  code?: string;
  showTime?: boolean;
  opacity?: number;
  hiddenLayer?: boolean;
  hiddenOpacity?: number;
  /** Khoá nhóm menu (nav-config) admin TẮT watermark; "home" = Tổng quan. */
  offPositions?: string[];
  version?: string;
}

/** Các dòng chữ lớp nhìn thấy: danh tính + "mã · ngày giờ". */
export function visibleLines(payload: WatermarkPayload, now: Date): string[] {
  if (!payload.code) return [];
  const tail = payload.showTime ? `${payload.code} · ${formatWatermarkTime(now)}` : payload.code;
  return [...(payload.lines ?? []).filter(Boolean), tail];
}

/** Lớp ẩn: mã + "dd/MM HHh" (đối chiếu với nhật ký truy cập), hoặc chỉ mã. */
export function hiddenLine(payload: WatermarkPayload, now: Date): string {
  if (!payload.code) return '';
  if (!payload.showTime) return payload.code;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${payload.code} ${pad(now.getDate())}/${pad(now.getMonth() + 1)} ${pad(now.getHours())}h`;
}

/** CSS nền (nhiều lớp) cho payload — dùng chung cho lớp phủ và khung xem trước. */
export function watermarkBackground(
  payload: WatermarkPayload,
  now: Date,
  dpr?: number
): { image: string; size: string } | null {
  if (!payload.enabled || !payload.code) return null;
  const layers: Tile[] = [];
  const visible = renderTile(
    {
      ...VISIBLE_TILE,
      lines: visibleLines(payload, now),
      opacity: clamp(payload.opacity, 0.05, 0.4),
    },
    dpr
  );
  if (visible) layers.push(visible);
  if (payload.hiddenLayer) {
    const hidden = renderTile(
      {
        ...HIDDEN_TILE,
        lines: [hiddenLine(payload, now)],
        opacity: clamp(payload.hiddenOpacity, 0.015, 0.08),
      },
      dpr
    );
    if (hidden) layers.push(hidden);
  }
  if (!layers.length) return null;
  return {
    image: layers.map((tile) => `url("${tile.url}")`).join(', '),
    size: layers.map((tile) => `${tile.width}px ${tile.height}px`).join(', '),
  };
}

function clamp(value: unknown, fallback: number, max: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.min(Math.max(n, 0), max);
}
